import * as React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { questions_list } from '../data/questions'
import {
  CategoryFeatureDictType,
  category_info,
  Feature,
} from '../data/features'
import { OptionId } from '../data/options'
import {
  getLocalStorageItem,
  setLocalStorageItem,
  ART_STYLE_DATA,
  QUIZ_ANSWERS,
  QUIZ_INDEX,
} from '../utils/localStorage'
import { getStyleData, getStyle } from '../utils/functions'

import { QueryContext } from './queryContext'

export type AnswerType = {
  [key in OptionId]?: boolean
}

/**
 * { [ category ]: Feature }
 */
export type QuizResults = {
  [category: string]: Feature
  // podcastGenre: Category1FeatureType
  // hostingStyle: Category2FeatureType
  // publishingSchedule: Category3FeatureType
  // podcastTone: Category4FeatureType
}

export type QuizContextState = {
  artStyleData: CategoryFeatureDictType
  selectedArtwork: string[]
  newHistory?: boolean // for when we go back
  // get all the options for the quiz and set the answer to false
  // quizAnswers = [ {"A":true,"B":false,"C":undefined], [], [], etc for each quesitons
  /**
   * [
   *  {A: true, B: false, etc},
   *  {C: false, D: true},
   *  ...
   * ]
   */
  quizAnswers: AnswerType[]
  quizIndex: number
  quizIndexHistory: number[]
  quizResults: QuizResults
}

export const DEFAULT_STYLE_RESULTS: QuizResults = Object.entries(
  category_info,
).reduce(
  (accu, [category, feature_info], idx) => ({
    ...accu,
    [category]: Object.keys(feature_info)[0],
  }),
  {} as QuizResults,
)
// {
//   podcastGenre: CATEGORY_1_FEATURES[0],
//   hostingStyle: CATEGORY_2_FEATURES[0],
//   publishingSchedule: CATEGORY_3_FEATURES[0],
//   podcastTone: CATEGORY_4_FEATURES[0],
// }

const INITIAL_QUIZ_ANSWERS = Object.keys(questions_list).map(
  () => ({} as AnswerType),
)

const storedArtStyleData = getLocalStorageItem(ART_STYLE_DATA)
const storedQuizAnswers = getLocalStorageItem(QUIZ_ANSWERS)
const storedQuizIndex = getLocalStorageItem(QUIZ_INDEX)

const initialState: QuizContextState = {
  artStyleData: storedArtStyleData ?? {},
  newHistory: false, // for when we go back
  // get all the options for the quiz and set the answer to false
  // quizAnswers = [ {"A":true,"B":false,"C":undefined], [], [], etc for each quesitons
  selectedArtwork: [],
  /**
   * [
   *  {A: true, B: false, etc},
   *  {C: false, D: true},
   *  ...
   * ]
   */
  quizAnswers: storedQuizAnswers ?? [...INITIAL_QUIZ_ANSWERS],
  quizIndex: Math.min(storedQuizIndex ?? 0, INITIAL_QUIZ_ANSWERS.length - 1),
  // quizAnswers: [...INITIAL_QUIZ_ANSWERS],
  // quizIndex: 0,
  quizIndexHistory: [],
  quizResults: DEFAULT_STYLE_RESULTS,
}

export const QuizStoreContext = React.createContext(initialState)
export const QuizDispatchContext = React.createContext(
  (() => {}) as React.Dispatch<QuizContextAction>,
)

export type QuizContextAction =
  | {
      type: 'initialize'
      payload: {
        artStyleData: CategoryFeatureDictType
        quizAnswers: Array<AnswerType>
        quizIndex: number
      }
    }
  | { type: 'increment_quiz_index' }
  | { type: 'decrement_quiz_index' }
  | { type: 'set_quiz_index'; payload: { quizIndex: number } }
  | { type: 'back_quiz_index' }
  | {
      type: 'update_quiz_answers_via_toggle'
      payload: { currentQuizIndex: number; key: OptionId }
    }
  | {
      type: 'update_quiz_answers'
      payload: {
        currentQuizIndex: number
        key: OptionId
        value: boolean
      }
    }
  | { type: 'reset_quiz' }
  | {
      type: 'set_art_style_data'
      payload: {
        artStyleData: CategoryFeatureDictType
        selectedArtwork: Array<string>
        quizResults: QuizResults
      }
    }

type QuizReducer = (
  state: QuizContextState,
  action: QuizContextAction,
) => QuizContextState

const reducer: QuizReducer = (
  state: QuizContextState,
  action: QuizContextAction,
): QuizContextState => {
  let newQuizIndex: number = state.quizIndex
  let newQuizAnswers: Array<AnswerType>
  // console.log('reducer:', action, state)

  switch (action.type) {
    case 'initialize':
      if (
        action.payload.quizIndex !== null &&
        action.payload.quizIndex !== undefined
      ) {
        newQuizIndex = action.payload.quizIndex
      }
      const newState = {
        ...state,
        artStyleData: getLocalStorageItem(ART_STYLE_DATA),
        quizIndex: newQuizIndex,
        quizIndexHistory: Array.from({ length: newQuizIndex + 1 }, (v, i) => i),
        quizAnswers: action.payload.quizAnswers || state.quizAnswers,
      }
      // console.log('initialize', state, newState)
      return newState
    case 'increment_quiz_index':
      newQuizIndex = Math.min(
        state.quizIndex + 1,
        Object.keys(questions_list).length - 1,
      )
      return {
        ...state,
        quizIndexHistory: [...state.quizIndexHistory, newQuizIndex],
        quizIndex: newQuizIndex,
        newHistory: true,
      }
    case 'decrement_quiz_index':
      newQuizIndex = Math.min(
        state.quizIndex - 1,
        Object.keys(questions_list).length - 1,
      )
      return {
        ...state,
        quizIndexHistory: [...state.quizIndexHistory, newQuizIndex],
        quizIndex: newQuizIndex,
        newHistory: true,
      }
    case 'set_quiz_index':
      if (
        state.quizIndexHistory[state.quizIndexHistory.length - 1] ===
        action.payload.quizIndex
      ) {
        return state
      } else {
        return {
          ...state,
          quizIndexHistory: [
            ...state.quizIndexHistory,
            action.payload.quizIndex,
          ],
          quizIndex: action.payload.quizIndex,
          newHistory: true,
        }
      }
    case 'back_quiz_index':
      if (state.quizIndexHistory.length > 1) {
        return {
          ...state,
          quizIndexHistory: state.quizIndexHistory.slice(0, -1),
          quizIndex: state.quizIndexHistory.slice(-2, -1)[0],
          newHistory: false,
        }
      } else {
        return state
      }
    case 'update_quiz_answers':
      // merge the new quiz answers with the old
      newQuizAnswers = [...state.quizAnswers]
      newQuizAnswers[action.payload.currentQuizIndex][action.payload.key] =
        action.payload.value
      // console.log(`newQuizAnswers: ${JSON.stringify(newQuizAnswers)}`)
      return { ...state, quizAnswers: newQuizAnswers } // main point

    case 'update_quiz_answers_via_toggle':
      newQuizAnswers = [...state.quizAnswers]
      newQuizAnswers[action.payload.currentQuizIndex] = Object.keys(
        newQuizAnswers[action.payload.currentQuizIndex],
      ).reduce((accu, key) => ({ ...accu, [key]: false }), {})
      newQuizAnswers[action.payload.currentQuizIndex][action.payload.key] = true

      // Check if anything has been changed
      if (
        JSON.stringify(newQuizAnswers) === JSON.stringify(state.quizAnswers)
      ) {
        return state
      } else {
        return { ...state, quizAnswers: newQuizAnswers } // main point
      }

    case 'set_art_style_data':
      return {
        ...state,
        ...action.payload,
      }
    case 'reset_quiz':
      return {
        ...state,
        quizIndex: 0,
        quizAnswers: [...INITIAL_QUIZ_ANSWERS],
      }
    default:
      throw Error('Unknown reduceQuizData action')
  }
}

type QuizControllerType = {
  children: React.ReactNode
}

export const QuizController: React.FC<QuizControllerType> = ({
  children,
}): JSX.Element => {
  // console.log('QuizController rerender')

  const history = useHistory()
  const location = useLocation()
  const [quizData, reduceQuizData]: [
    QuizContextState,
    React.Dispatch<QuizContextAction>,
  ] = React.useReducer<QuizReducer>(reducer, initialState) // assign for shallow copy
  // const value: [
  //   QuizContextState,
  //   React.Dispatch<QuizContextAction>,
  // ] = React.useMemo(() => [quizData, reduceQuizData], [quizData])

  const [query] = React.useContext(QueryContext)

  /**
   * if newHistory changes, then refresh?
   */
  React.useEffect(() => {
    if (quizData.newHistory) {
      history.push(location.pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizData.quizIndex, quizData.newHistory])

  /**
   * Store changes to quiz index in localStorage
   */
  React.useEffect(() => {
    // console.log('QuizController useEffect([quizIndex])')
    setLocalStorageItem(QUIZ_INDEX, quizData.quizIndex)
  }, [quizData.quizIndex])

  // Run this before the UI updates
  React.useLayoutEffect(() => {
    // console.log('QuizController useEffect([quizData.quizAnswers, query])')
    // if (JSON.stringify(quizData.quizAnswers) === JSON.stringify(quizData.quizAnswers)){
    //   return
    // }
    // Updating answers will update the styleData
    const artStyleData = getStyleData(quizData.quizAnswers)
    const artStyle = getStyle(artStyleData)
    // console.log('artStyle', artStyle)
    // const artStyleFromQuery = getStyleFromQuery(query)
    // console.log(`setting Art Style ${JSON.stringify(artStyle)}`)
    // console.log(`setting Art Style from query ${JSON.stringify(artStyleFromQuery)}`)
    // const { podcastGenre, hostingStyle, publishingSchedule, podcastTone } = {
    //   ...artStyle,
    //   ...artStyleFromQuery,
    // }

    const selectedArtwork = quizData.quizAnswers.reduce(
      (accu: Array<string>, answer_set) => [
        ...accu,
        ...Object.keys(answer_set).filter((a) =>
          Boolean(answer_set[a as OptionId]),
        ),
      ],
      [],
    )

    reduceQuizData({
      type: 'set_art_style_data',
      payload: {
        artStyleData: artStyleData,
        // podcastGenre,
        // hostingStyle,
        // publishingSchedule,
        // podcastTone,
        selectedArtwork: selectedArtwork,
        quizResults: artStyle,
      },
    })

    setLocalStorageItem(QUIZ_ANSWERS, quizData.quizAnswers)
    setLocalStorageItem(ART_STYLE_DATA, artStyleData)
  }, [quizData.quizAnswers, query])

  // React.useEffect(() => {
  //   console.log('query changes:', query)
  // }, [query])

  // React.useEffect(() => {
  //   console.log('quizData.quizAnswers changes:', quizData.quizAnswers)
  // }, [quizData.quizAnswers])

  // React.useEffect(() => {
  //   console.log('quizData changes:', quizData)
  // }, [quizData])

  // React.useEffect(() => {
  //   console.log('history changes:', history)
  // }, [history])

  // React.useEffect(() => {
  //   console.log('location changes:', location)
  // }, [location])

  // return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>
  return (
    <QuizDispatchContext.Provider value={reduceQuizData}>
      <QuizStoreContext.Provider value={quizData}>
        {children}
      </QuizStoreContext.Provider>
    </QuizDispatchContext.Provider>
  )
}
